/**
 * Tela de formulário de um pedido de compra, em endereço próprio.
 *
 * Papel: monta `/compras/novo` e `/compras/:id`, com a mesma verificação de
 * nível `editar` que `PaginaCompras` aplica aos botões do quadro — aqui ela
 * decide se o formulário chega a aparecer, porque o endereço é digitável
 * direto, sem passar pelo botão que o esconderia.
 *
 * Como usar: o `id` vem de `useParams`; ausente, a tela cria um pedido novo.
 * Presente e sem correspondência entre os pedidos carregados, a tela mostra que
 * o registro não existe, em vez de abrir um formulário em branco sob um número
 * que não é o dele — o que cobre tanto o `id` apagado por outra aba quanto o
 * digitado errado.
 *
 * As duas consultas de que `FormularioPedido` depende — a listagem de pedidos e
 * `useResidencias` — são esperadas **aqui, em qualquer um dos dois modos**,
 * antes de o formulário chegar a montar, e é aqui que o número sugerido, a casa
 * pré-escolhida, a data de hoje e as listas de sugestão são calculados.
 * `FormularioPedido` lê esses valores uma vez só, no `defaultValues` do
 * primeiro render; montá-lo com qualquer uma das consultas ainda a caminho
 * trava o número em `PC-001/AAAA` e a casa em branco, porque o react-hook-form
 * não refaz esse cálculo depois.
 *
 * As casas oferecidas passam por `casasQueRecebemRegistro`: só as que não
 * contêm outra, mais a do pedido aberto — um pedido gravado no lugar que
 * contém a casa lançaria a despesa fora do caixa. Quando `useResidencias`
 * falha — quem não tem `ver` em Cadastros-base recebe 403 dela —, o formulário
 * abre assim mesmo, com as casas tiradas dos pedidos carregados por
 * `casasDistintas`, e cada `Chip` mostra só o nome, porque o pedido não traz
 * cidade nem UF. A falha da listagem de pedidos continua trocando o formulário
 * pelo aviso de erro: sem ela não há número nem, na edição, o próprio pedido.
 * A casa sugerida para um pedido novo é a do mais recente cuja casa está entre
 * as oferecidas, e a primeira delas quando nenhum tem.
 *
 * `useClassificacaoFinanceira` **não** é esperada: ela não entra em
 * `defaultValues`, só nas opções dos selects de categoria e no
 * autopreenchimento pelo fornecedor, que passam a valer quando ela responde.
 * Enquanto isso o formulário recebe `CLASSIFICACAO_VAZIA`; se ela falhar, um
 * aviso acima do formulário diz por que os selects estão vazios — sem ele, a
 * virada para comprado seria recusada por falta de categoria sem explicação.
 * Os favorecidos do caixa entram também na sugestão de "Nome do fornecedor".
 *
 * Imprimir, duplicar e excluir vivem aqui, ao lado do título, e não dentro de
 * `FormularioPedido`: são ações sobre o registro, e um pedido novo só ganha as
 * duas últimas depois de existir. "🖨 Imprimir / PDF" imprime a
 * `FolhaDeAprovacao` do que está na tela, edição não salva inclusa, e nunca a
 * tela em si; a mesma folha sai quando o `Chip` de etapa tira o rascunho da
 * cotação para a aprovação, também sem gravar. As fotos dos itens são buscadas
 * antes, e a falha dessa busca aparece no aviso de erro, sem folha.
 * Enquanto a folha está montada, o resto da tela leva `print:hidden`. "Duplicar" lê os valores atuais pela `ref` —
 * `alcaDoFormulario` —, e não o `registro` gravado, para copiar também o que a
 * pessoa tiver alterado e ainda não salvo. Toda saída depois de gravar ou
 * excluir volta à lista com `replace`, para o Voltar do navegador não cair de
 * novo num formulário já salvo ou num pedido que deixou de existir.
 */
import {
  type NovoPedidoCompra,
  acessoAlcanca,
  novoPedidoCompraSchema,
  proximoNumero,
} from '@concierge/shared';
import { type ClassificacaoFinanceira, type PedidoCompra } from '@concierge/shared';
import { type ReactNode, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';
import { cn } from '@/shared/lib/utils';
import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import { CabecalhoDoModulo } from '@/shared/ui/cabecalho-do-modulo';
import {
  casasDistintas,
  casasQueRecebemRegistro,
  useResidencias,
} from '@/modules/cadastros-base';
import { buscarFotoDoItem } from '../api/compras';
import { FolhaDeAprovacao } from '../components/folha-de-aprovacao';
import {
  type AlcaDoFormularioPedido,
  type CasaDoPedido,
  FormularioPedido,
} from '../components/formulario-pedido';
import { rascunhoParaFolha } from '../folha-de-aprovacao';
import { type DespesaAvisada, estadoComDespesas } from '../lancado-no-financeiro';
import {
  useClassificacaoFinanceira,
  useExcluirPedido,
  usePedidosCompra,
  useSalvarPedido,
} from '../hooks/usar-compras';
import { useFolhaDeAprovacao } from '../hooks/usar-folha-de-aprovacao';
import { type PedidoNoFormulario } from '../types';

/**
 * O próximo número de pedido, `PC-NNN/AAAA`, pela contagem dos já gravados no
 * ano corrente.
 *
 * É sugestão por contagem, e não identidade: apagar um pedido faz o número dele
 * voltar a ser oferecido, e dois pedidos podem terminar com o mesmo número.
 *
 * @param numeros - Os números já gravados, em qualquer ordem.
 * @returns O número do próximo pedido.
 */
function proximoNumeroDePedido(numeros: readonly string[]): string {
  return `PC-${proximoNumero(numeros, new Date().getFullYear())}`;
}

/**
 * A classificação com que o formulário abre enquanto a consulta não respondeu,
 * ou quando ela falhou: os selects oferecem só "— Selecionar —", e o
 * fornecedor não preenche nada. O pedido continua podendo ser gravado — a
 * categoria só é exigida na virada para comprado.
 */
const CLASSIFICACAO_VAZIA: ClassificacaoFinanceira = {
  categorias: [],
  favorecidos: [],
  padroes: [],
};

/**
 * Os textos distintos de uma lista, em ordem alfabética, para as sugestões.
 *
 * @param valores - Os textos, com nulos e repetições.
 * @returns Os textos distintos, sem nulos.
 */
function distintos(valores: readonly (string | null)[]): string[] {
  const preenchidos = valores.filter(
    (valor): valor is string => valor !== null && valor !== '',
  );
  return [...new Set(preenchidos)].sort((um, outro) => um.localeCompare(outro, 'pt-BR'));
}

/**
 * O cabeçalho da tela e um aviso no lugar do formulário.
 *
 * @param titulo - O título do aviso, em serifa.
 * @param children - A explicação e, quando houver, o botão de tentar de novo.
 * @returns O cabeçalho com o Voltar e o bloco do aviso.
 */
function TelaDeAviso({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div>
      <CabecalhoDoModulo titulo="Pedido de compra" voltarPara="/compras" />
      <div className="rounded-md border border-dashed border-border bg-card px-6 py-12 text-center">
        <p className="font-serif text-lg text-foreground">{titulo}</p>
        {children}
      </div>
    </div>
  );
}

/**
 * O aviso de que a listagem de pedidos, sem a qual o formulário não abre, falhou.
 *
 * @param erro - O que a consulta rejeitou.
 * @param onTentarDeNovo - Chamado para repetir a consulta que falhou.
 * @returns O cabeçalho e a explicação da falha, com o botão de tentar de novo.
 */
function TelaDeErroDeConsulta({
  erro,
  onTentarDeNovo,
}: {
  erro: unknown;
  onTentarDeNovo: () => void;
}) {
  return (
    <TelaDeAviso titulo="Não foi possível carregar">
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        {erro instanceof ErroDaApi
          ? erro.message
          : 'A consulta falhou antes de chegar ao servidor.'}
      </p>
      <Button variant="outline" className="mt-4" onClick={onTentarDeNovo}>
        Tentar de novo
      </Button>
    </TelaDeAviso>
  );
}

/**
 * O cabeçalho da tela e uma mensagem de espera, sem formulário nenhum.
 *
 * @param mensagem - O que dizer enquanto se espera.
 * @returns O cabeçalho e a mensagem.
 */
function TelaDeEspera({ mensagem }: { mensagem: string }) {
  return (
    <div>
      <CabecalhoDoModulo titulo="Pedido de compra" voltarPara="/compras" />
      <p className="text-sm text-muted-foreground">{mensagem}</p>
    </div>
  );
}

/**
 * Página do formulário de pedido de compra.
 *
 * @returns O cabeçalho com as ações do registro e o formulário, ou o estado que
 *   a permissão, o carregamento ou a busca pelo `id` impuserem.
 */
export function PaginaFormularioPedido() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [aRemover, setARemover] = useState(false);
  const [erroDaAcao, setErroDaAcao] = useState<string | null>(null);
  const alcaDoFormulario = useRef<AlcaDoFormularioPedido>(null);

  const acesso = useAcesso();
  const podeEditar =
    acesso.data !== undefined && acessoAlcanca(acesso.data, 'compras', 'editar');

  const consultaPedidos = usePedidosCompra();
  const consultaCasas = useResidencias();
  const consultaClassificacao = useClassificacaoFinanceira();
  const duplicar = useSalvarPedido();
  const excluir = useExcluirPedido();
  const folhaDeAprovacao = useFolhaDeAprovacao();

  /** Volta para a lista depois de gravar ou excluir, sem deixar o formulário no histórico. */
  function handleVoltarParaLista() {
    void navigate('/compras', { replace: true });
  }

  /**
   * Volta para a lista depois de gravar, levando as despesas que a gravação
   * lançou, para o quadro abrir "Lançado no Financeiro" com elas.
   *
   * @param despesasLancadas - O que `FormularioPedido` entregou; vazia, a volta
   *   é a mesma de `handleVoltarParaLista`.
   */
  function handleSalvo(despesasLancadas: DespesaAvisada[]) {
    if (despesasLancadas.length === 0) {
      handleVoltarParaLista();
      return;
    }
    void navigate('/compras', { replace: true, state: estadoComDespesas(despesasLancadas) });
  }

  /** Volta para a lista sem gravar nada, como o Voltar do cabeçalho. */
  function handleCancelar() {
    void navigate('/compras');
  }

  if (acesso.isPending) return <TelaDeEspera mensagem="Verificando permissão…" />;

  if (!podeEditar) {
    return (
      <TelaDeAviso titulo="Acesso restrito">
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Esta tela exige o nível editar em Compras & estoque. Se você precisa dela, peça o
          nível a quem administra.
        </p>
      </TelaDeAviso>
    );
  }

  if (consultaPedidos.isPending || consultaCasas.isPending) {
    return <TelaDeEspera mensagem="Carregando…" />;
  }

  if (consultaPedidos.error !== null) {
    return (
      <TelaDeErroDeConsulta
        erro={consultaPedidos.error}
        onTentarDeNovo={() => void consultaPedidos.refetch()}
      />
    );
  }

  const pedidos = consultaPedidos.data;
  const encontrado = id === undefined ? undefined : pedidos.find((pedido) => pedido.id === id);
  const casaDoRegistro = encontrado?.residencia ?? null;
  const casasConhecidas: readonly CasaDoPedido[] =
    consultaCasas.data ??
    casasDistintas([...pedidos.map((pedido) => pedido.residencia), casaDoRegistro]);
  const casas = casasQueRecebemRegistro(casasConhecidas, casaDoRegistro?.id ?? null);
  const idsOferecidos = new Set(casas.map((casa) => casa.id));
  const classificacao = consultaClassificacao.data ?? CLASSIFICACAO_VAZIA;
  const fornecedores = distintos([
    ...pedidos.flatMap((p) => p.itens.map((i) => i.fornecedor)),
    ...classificacao.favorecidos.map((favorecido) => favorecido.nome),
  ]);
  const solicitantes = distintos(pedidos.map((p) => p.solicitante));
  const numeroSugerido = proximoNumeroDePedido(pedidos.map((p) => p.numero));
  // A casa sugerida é a do pedido mais recente que tem uma entre as oferecidas:
  // o pedido sem casa é exceção escolhida à mão, e não o padrão de um pedido
  // novo, e a casa que não está entre os `Chip` não teria como ser desmarcada.
  /**
   * Gera a folha de aprovação dos valores do formulário, sem gravar nada.
   *
   * @param valores - O que está na tela, na etapa em que está.
   * @param gravado - O pedido aberto, de onde vêm as fotos não trocadas, ou
   *   `null` num pedido novo.
   */
  async function gerarFolhaDoRascunho(
    valores: PedidoNoFormulario,
    gravado: PedidoCompra | null,
  ) {
    setErroDaAcao(null);
    try {
      await folhaDeAprovacao.gerar([
        rascunhoParaFolha(valores, { casas: casasConhecidas, gravado }),
      ]);
    } catch {
      setErroDaAcao(
        'A folha de aprovação não saiu: não foi possível carregar as fotos dos itens.',
      );
    }
  }

  /**
   * Imprime a folha de aprovação do que está na tela.
   *
   * @param gravado - O pedido aberto, ou `null` num pedido novo.
   */
  function handleImprimir(gravado: PedidoCompra | null) {
    const valores = alcaDoFormulario.current?.obterValores();
    if (valores !== undefined) void gerarFolhaDoRascunho(valores, gravado);
  }

  /** A folha montada, fora do bloco da tela, ou nada. */
  const folha =
    folhaDeAprovacao.folha === null ? null : (
      <FolhaDeAprovacao
        key={folhaDeAprovacao.folha.geradaEm}
        pedidos={folhaDeAprovacao.folha.pedidos}
        geradaEm={folhaDeAprovacao.folha.geradaEm}
        onConcluida={folhaDeAprovacao.concluir}
      />
    );
  const escondidaNaImpressao = cn(folhaDeAprovacao.folha !== null && 'print:hidden');

  // Sem a classificação, os selects de categoria ficam só com "— Selecionar —",
  // e a virada para comprado seria recusada sem que a tela dissesse por quê.
  const avisoDaClassificacao = consultaClassificacao.error !== null && (
    <p className="mb-4 rounded-sm border border-perigo/40 bg-perigo/5 p-3 text-sm text-perigo">
      Não foi possível carregar as categorias do Financeiro. Recarregue a página para escolher
      a categoria de cada item.
    </p>
  );

  const sugestao = {
    residenciaId:
      pedidos.find(
        (pedido) => pedido.residencia !== null && idsOferecidos.has(pedido.residencia.id),
      )?.residencia?.id ??
      casas[0]?.id ??
      '',
    numero: numeroSugerido,
    data: hojeEmIso(),
  };

  if (id === undefined) {
    return (
      <>
        <div className={escondidaNaImpressao}>
          <CabecalhoDoModulo
            titulo="Novo pedido de compra"
            subtitulo={numeroSugerido}
            voltarPara="/compras"
            acoes={
              <Button type="button" variant="outline" onClick={() => handleImprimir(null)}>
                🖨 Imprimir / PDF
              </Button>
            }
          />

          {erroDaAcao !== null && (
            <p
              role="alert"
              className="mb-4 rounded-sm border border-perigo/40 bg-perigo/5 p-3 text-sm text-perigo"
            >
              {erroDaAcao}
            </p>
          )}
          {avisoDaClassificacao}

          <FormularioPedido
            ref={alcaDoFormulario}
            key="novo"
            pedido={null}
            sugestao={sugestao}
            casas={casas}
            fornecedores={fornecedores}
            classificacao={classificacao}
            solicitantes={solicitantes}
            onSairDaCotacao={(valores) => void gerarFolhaDoRascunho(valores, null)}
            onSalvo={handleSalvo}
            onCancelar={handleCancelar}
          />
        </div>
        {folha}
      </>
    );
  }

  if (encontrado === undefined) {
    return (
      <TelaDeAviso titulo="Pedido não encontrado">
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Ele pode ter sido excluído nesta ou em outra sessão.
        </p>
      </TelaDeAviso>
    );
  }

  // Alias com o tipo já estreitado: `encontrado` segue como `PedidoCompra |
  // undefined` para o TypeScript dentro das funções abaixo, porque a
  // verificação acima não atravessa o limite de uma função aninhada.
  const registro = encontrado;

  /**
   * Copia o que está na tela — inclusive edição que ninguém salvou ainda —
   * num pedido novo, com número novo, de volta à cotação e sem nenhuma decisão
   * nem data de lançamento.
   *
   * A foto de um item que já tinha uma, e que a pessoa não trocou nesta
   * sessão, não está nos valores do formulário — `FormularioPedido` não a
   * carrega ao abrir —, e por isso é buscada aqui antes de copiar; sem essa
   * busca, o pedido duplicado perderia a foto em silêncio. Se a busca falhar, a
   * cópia inteira para e mostra o erro.
   */
  async function handleDuplicar() {
    setErroDaAcao(null);

    const valoresAtuais = alcaDoFormulario.current?.obterValores();
    if (valoresAtuais === undefined) return;

    let dados: NovoPedidoCompra;
    try {
      dados = novoPedidoCompraSchema.parse(valoresAtuais);
    } catch {
      setErroDaAcao('Corrija os campos com erro antes de duplicar.');
      return;
    }

    try {
      const itens = await Promise.all(
        dados.itens.map(async (item) => {
          const { id: idDoItem, ...resto } = item;
          const gravado =
            idDoItem === undefined
              ? undefined
              : registro.itens.find((original) => original.id === idDoItem);
          const semData = { ...resto, dataCompra: null };

          // Ausente é o único estado que o formulário produz para um item que
          // já tinha foto e não foi trocado: buscar aqui é o que evita a cópia
          // sair sem a imagem que a pessoa nunca pediu para tirar.
          if (resto.foto === undefined && gravado?.temFoto === true) {
            const { foto } = await buscarFotoDoItem(gravado.id);
            return { ...semData, foto: foto ?? undefined };
          }
          return semData;
        }),
      );

      await duplicar.mutateAsync({
        id: null,
        dados: {
          ...dados,
          numero: proximoNumeroDePedido(pedidos.map((p) => p.numero)),
          status: 'em_cotacao',
          aprovador: null,
          dataAprovacao: null,
          compradoPor: null,
          motivoReprovacao: null,
          lancadoNoFinanceiro: false,
          itens,
        },
      });
      handleVoltarParaLista();
    } catch (erro) {
      setErroDaAcao(
        erro instanceof ErroDaApi
          ? erro.message
          : 'Não foi possível copiar uma das fotos, ou a duplicação falhou antes de chegar ao servidor.',
      );
    }
  }

  /** Apaga o pedido aberto e volta para a lista, se a API aceitar. */
  async function handleConfirmarRemocao() {
    setErroDaAcao(null);
    try {
      await excluir.mutateAsync(registro.id);
      handleVoltarParaLista();
    } catch (erro) {
      setARemover(false);
      setErroDaAcao(
        erro instanceof ErroDaApi
          ? erro.message
          : 'A exclusão falhou antes de chegar ao servidor.',
      );
    }
  }

  return (
    <>
      <div className={escondidaNaImpressao}>
        <CabecalhoDoModulo
          titulo="Editar pedido de compra"
          subtitulo={registro.numero}
          voltarPara="/compras"
          acoes={
            <>
              <Button type="button" variant="outline" onClick={() => handleImprimir(registro)}>
                🖨 Imprimir / PDF
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleDuplicar()}
                disabled={duplicar.isPending}
              >
                ⧉ Duplicar
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setARemover(true)}
                disabled={excluir.isPending}
              >
                🗑 Excluir
              </Button>
            </>
          }
        />

        {erroDaAcao !== null && (
          <p
            role="alert"
            className="mb-4 rounded-sm border border-perigo/40 bg-perigo/5 p-3 text-sm text-perigo"
          >
            {erroDaAcao}
          </p>
        )}
        {avisoDaClassificacao}

        <FormularioPedido
          ref={alcaDoFormulario}
          key={registro.id}
          pedido={registro}
          sugestao={sugestao}
          casas={casas}
          fornecedores={fornecedores}
          classificacao={classificacao}
          solicitantes={solicitantes}
          onSairDaCotacao={(valores) => void gerarFolhaDoRascunho(valores, registro)}
          onSalvo={handleSalvo}
          onCancelar={handleCancelar}
        />

        <AlertDialog open={aRemover} onOpenChange={setARemover}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir o pedido {registro.numero}?</AlertDialogTitle>
              <AlertDialogDescription>
                Os itens do pedido vão junto, com as imagens deles. A ação não pode ser
                desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel disabled={excluir.isPending}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={(evento) => {
                  evento.preventDefault();
                  void handleConfirmarRemocao();
                }}
                disabled={excluir.isPending}
              >
                {excluir.isPending ? 'Excluindo…' : 'Excluir'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {folha}
    </>
  );
}
